"use client";

import {
  ArchiveIcon,
  EyeIcon,
  FolderIcon,
  HashIcon,
  MailIcon,
  MessagesSquareIcon,
  MailOpenIcon,
  PauseCircleIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlayCircleIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react";
import Link from "next/link";

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import type {
  WorkspaceChannel,
  WorkspaceSession,
} from "@/lib/mlx-workspace-client";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ProvenanceAvatarStack,
  channelClientProvenance,
  clientAppLabel,
  interfaceLabel,
  sessionClientProvenance,
  type ProvenanceActor,
} from "@/components/app/runs/provenance-avatar-stack";
import {
  workspaceSessionActionAvailability,
  workspaceSessionTitle,
} from "./workspace-navigation-model";
import { LiveSessionTitle } from "@/components/app/sessions/live-session-title";
import { ExternalChannelBadge } from "./external-channel-badge";
import {
  EntityCardBody,
  EntityCardFooter,
  EntityCardHeader,
} from "@/components/content/entity-card";

export type SessionNavigationImmediateAction = "pause" | "resume" | "cancel";

const NAVIGATION_STATUS_DOT_CLASS = "size-2 shrink-0 rounded-full";

function navigationRowState(active: boolean) {
  return active
    ? "border-transparent bg-primary/[0.08] text-primary"
    : "border-transparent hover:border-primary/20 hover:bg-primary/10 hover:text-primary";
}

export function SessionNavigationRow(props: {
  active: boolean;
  pending: boolean;
  pinned: boolean;
  unread: boolean;
  session: WorkspaceSession;
  provenanceActor?: ProvenanceActor | null;
  snippet?: string;
  onArchive: () => void;
  onDelete: () => void;
  onPinChange: () => void;
  onReadChange: () => void;
  onRename: () => void;
  onSessionAction: (action: SessionNavigationImmediateAction) => void;
}) {
  const href = `/workspace/sessions/${encodeURIComponent(props.session.id)}`;
  const availability = workspaceSessionActionAvailability(props.session.status);
  const title = workspaceSessionTitle(props.session);
  const provenance = sessionClientProvenance(props.session.metadata);
  return (
    <ContextMenu disabled={props.pending}>
      <ContextMenuTrigger
        render={
          <div
            className={cn(
              "group relative mx-1 rounded-md border text-sm leading-[18px] transition-colors",
              navigationRowState(props.active),
              props.pending && "pointer-events-none opacity-55",
            )}
          />
        }
      >
        <TooltipProvider delay={300}>
          <Tooltip>
            <TooltipTrigger
              render={
                <Link
                  aria-current={props.active ? "page" : undefined}
                  className={cn(
                    "flex h-[30px] min-w-0 items-center gap-2 pl-2 pr-2",
                    props.session.status === "running" && "pr-8",
                  )}
                  href={href}
                />
              }
            >
              <LiveSessionTitle
                className="min-w-0 flex-1 truncate font-medium"
                title={title}
              />
              {/* A dot only for something new; a read row carries none, and
                  pulsing is kept for work in progress. */}
              {props.unread ? (
                <span
                  aria-label="Unread"
                  className={cn(NAVIGATION_STATUS_DOT_CLASS, "bg-badge-sky")}
                />
              ) : null}
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={10}
              className="block w-72 overflow-hidden rounded-xl border border-border/70 bg-popover p-0 text-popover-foreground shadow-xl shadow-black/10"
            >
              <NavigationEntityPreview
                actor={props.provenanceActor}
                action="started"
                description={props.snippet}
                kind="Session"
                provenance={provenance}
                title={title}
              />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {props.session.status === "running" ? (
          <span
            aria-label="Running"
            className="pointer-events-none absolute right-0 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-muted-foreground transition-opacity group-hover:opacity-0"
            title="Running"
          >
            <Spinner className="size-3.5" strokeWidth={1.75} />
          </span>
        ) : null}
        <button
          aria-label={`Archive ${title}`}
          className="absolute right-0 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md bg-background/85 text-muted-foreground opacity-0 shadow-[-6px_0_8px_-4px_hsl(var(--background))] backdrop-blur-md transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
          disabled={props.pending}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            props.onArchive();
          }}
          type="button"
        >
          <ArchiveIcon className="size-3.5" />
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem render={<Link href={href} />}>
          <EyeIcon />
          Open
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onPinChange}>
          {props.pinned ? <PinOffIcon /> : <PinIcon />}
          {props.pinned ? "Unpin" : "Pin"}
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onRename}>
          <PencilIcon />
          Rename
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onReadChange}>
          {props.unread ? <MailOpenIcon /> : <MailIcon />}
          {props.unread ? "Mark as read" : "Mark as unread"}
        </ContextMenuItem>
        {availability.canResume ||
        availability.canPause ||
        availability.canCancel ? (
          <ContextMenuSeparator />
        ) : null}
        {availability.canResume ? (
          <ContextMenuItem onClick={() => props.onSessionAction("resume")}>
            <PlayCircleIcon />
            Resume
          </ContextMenuItem>
        ) : null}
        {availability.canPause ? (
          <ContextMenuItem onClick={() => props.onSessionAction("pause")}>
            <PauseCircleIcon />
            Pause
          </ContextMenuItem>
        ) : null}
        {availability.canCancel ? (
          <ContextMenuItem
            onClick={() => props.onSessionAction("cancel")}
            variant="destructive"
          >
            <XCircleIcon />
            Cancel
          </ContextMenuItem>
        ) : null}
        <ContextMenuSeparator />
        <ContextMenuItem onClick={props.onArchive}>
          <ArchiveIcon />
          Archive
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onDelete} variant="destructive">
          <Trash2Icon />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function navigationProvenanceText(
  provenance: ReturnType<typeof sessionClientProvenance>,
) {
  const app = clientAppLabel(provenance.clientApp);
  const via = provenance.interfaceType
    ? interfaceLabel(provenance.interfaceType)
    : "web app";
  return app ? `${app} via ${via}` : via;
}

/*
 * A sidebar row's preview, in the same card grammar as every other preview:
 * its mark and name, what it is about, and who started it and from where.
 */
function NavigationEntityPreview(props: {
  actor?: ProvenanceActor | null;
  action: "started" | "created";
  description?: string;
  kind: "Session" | "Channel";
  provenance: ReturnType<typeof sessionClientProvenance>;
  title: string;
}) {
  const actor = props.actor?.label ?? "Twin";
  const Glyph = props.kind === "Channel" ? HashIcon : MessagesSquareIcon;
  return (
    <div>
      <EntityCardHeader
        disc={<Glyph aria-hidden className="size-[50%] text-muted-foreground" />}
        title={props.title}
        subtitle={`${props.kind} · ${navigationProvenanceText(props.provenance)}`}
      />
      {props.description ? (
        <EntityCardBody>
          <p className="line-clamp-3 text-xs leading-[18px] text-muted-foreground">
            {props.description}
          </p>
        </EntityCardBody>
      ) : null}
      <EntityCardFooter
        source={
          <>
            <ProvenanceAvatarStack
              actor={props.actor}
              {...props.provenance}
              action={props.action}
              layout="overlay"
              size="sm"
            />
            <span className="min-w-0 truncate">
              {props.action === "started" ? "Started" : "Created"} by {actor}
            </span>
          </>
        }
      />
    </div>
  );
}

export function ChannelNavigationRow(props: {
  active: boolean;
  pending: boolean;
  pinned: boolean;
  running: boolean;
  channel: WorkspaceChannel;
  /** Marks a channel that admits guests; never set for a guest. */
  external?: boolean;
  provenanceActor?: ProvenanceActor | null;
  /** Absent for a guest, who cannot archive the channel. */
  onArchive?: () => void;
  /** Present when the deployment shows channel groups. */
  onMoveToGroup?: () => void;
  onPinChange: () => void;
  /** Absent for a guest, who cannot rename the channel. */
  onRename?: () => void;
}) {
  const href = `/workspace/channels/${encodeURIComponent(props.channel.id)}`;
  const provenance = channelClientProvenance(props.channel.metadata);
  return (
    <ContextMenu disabled={props.pending}>
      <ContextMenuTrigger
        render={
          <div
            className={cn(
              "group relative mx-1 rounded-md border text-sm leading-[18px] transition-colors",
              navigationRowState(props.active),
              props.pending && "pointer-events-none opacity-55",
            )}
          />
        }
      >
        <TooltipProvider delay={300}>
          <Tooltip>
            <TooltipTrigger
              render={
                <Link
                  aria-current={props.active ? "page" : undefined}
                  className="flex h-[30px] min-w-0 items-center gap-2 pl-2 pr-2"
                  href={href}
                />
              }
            >
              <HashIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate font-medium">
                {props.channel.name}
              </span>
              {props.external ? <ExternalChannelBadge size="xs" /> : null}
              {props.running ? (
                <span
                  aria-label="Session running"
                  className={cn(
                    NAVIGATION_STATUS_DOT_CLASS,
                    "animate-pulse bg-badge-sky",
                  )}
                />
              ) : null}
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={10}
              className="block w-72 overflow-hidden rounded-xl border border-border/70 bg-popover p-0 text-popover-foreground shadow-xl shadow-black/10"
            >
              <NavigationEntityPreview
                actor={props.provenanceActor}
                action="created"
                description={props.channel.purpose}
                kind="Channel"
                provenance={provenance}
                title={props.channel.name}
              />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem render={<Link href={href} />}>
          <EyeIcon />
          Open
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onPinChange}>
          {props.pinned ? <PinOffIcon /> : <PinIcon />}
          {props.pinned ? "Unpin" : "Pin"}
        </ContextMenuItem>
        {props.onRename ? (
          <ContextMenuItem onClick={props.onRename}>
            <PencilIcon />
            Rename
          </ContextMenuItem>
        ) : null}
        {props.onMoveToGroup ? (
          <ContextMenuItem onClick={props.onMoveToGroup}>
            <FolderIcon />
            Move to group…
          </ContextMenuItem>
        ) : null}
        {props.onArchive ? (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={props.onArchive}>
              <ArchiveIcon />
              Archive
            </ContextMenuItem>
          </>
        ) : null}
      </ContextMenuContent>
    </ContextMenu>
  );
}
